import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Combobox com busca — substitui os <select> nativos em listas longas (cidades,
// segmentos, regiões), onde rolar centenas de <option> sem filtrar é inviável.
// Ordena alfabeticamente (pt-BR, ignorando acento), navega por teclado e fecha
// ao clicar fora.
// ─────────────────────────────────────────────────────────────────────────────

export interface OpcaoSelect {
    value: string;
    label: string;
    /** Contagem opcional exibida à direita (ex.: nº de leads na cidade). */
    count?: number;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    options: OpcaoSelect[];
    /** Texto do item "todos" (value = ''). Se ausente, o campo é obrigatório. */
    allLabel?: string;
    placeholder?: string;
    /** Rótulo curto mostrado antes do valor (ex.: "Cidade"). */
    prefix?: string;
    className?: string;
    disabled?: boolean;
}

const norm = (s: string) =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const SearchableSelect: React.FC<Props> = ({
    value, onChange, options, allLabel, placeholder = 'Buscar…', prefix, className = '', disabled,
}) => {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState('');
    const [destaque, setDestaque] = useState(0);
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Ordem alfabética pt-BR (José antes de Juliana, acento não atrapalha)
    const ordenadas = useMemo(
        () => options.slice().sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })),
        [options],
    );

    const filtradas = useMemo(() => {
        const q = norm(busca.trim());
        const base = q ? ordenadas.filter((o) => norm(o.label).includes(q)) : ordenadas;
        return allLabel ? [{ value: '', label: allLabel } as OpcaoSelect, ...base] : base;
    }, [ordenadas, busca, allLabel]);

    const selecionada = options.find((o) => o.value === value);
    const textoBotao = selecionada?.label || allLabel || placeholder;

    // Fecha ao clicar fora
    useEffect(() => {
        if (!aberto) return;
        const onDoc = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [aberto]);

    // Ao abrir: foca a busca e posiciona o destaque no item atual
    useEffect(() => {
        if (!aberto) { setBusca(''); return; }
        const i = filtradas.findIndex((o) => o.value === value);
        setDestaque(i >= 0 ? i : 0);
        const t = setTimeout(() => inputRef.current?.focus(), 20);
        return () => clearTimeout(t);
    }, [aberto]);

    // Mantém o item destacado visível durante a navegação por teclado
    useEffect(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-i="${destaque}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [destaque]);

    const escolher = (v: string) => { onChange(v); setAberto(false); };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setDestaque((d) => Math.min(d + 1, filtradas.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setDestaque((d) => Math.max(d - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); const o = filtradas[destaque]; if (o) escolher(o.value); }
        else if (e.key === 'Escape') { e.preventDefault(); setAberto(false); }
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setAberto((v) => !v)}
                className={`w-full flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-xl border transition-all disabled:opacity-40
                    ${value
                        ? 'bg-primary/5 border-primary/40 text-primary'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'}`}
            >
                {prefix && <span className="text-gray-400 font-normal shrink-0">{prefix}:</span>}
                <span className="truncate flex-1 text-left">{textoBotao}</span>
                {selecionada?.count != null && (
                    <span className="text-[10px] px-1.5 rounded-full bg-primary/10 shrink-0">{selecionada.count}</span>
                )}
                {value && (
                    <span
                        role="button"
                        aria-label="Limpar"
                        onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                    >
                        <X size={12} />
                    </span>
                )}
                <ChevronDown size={13} className={`text-gray-400 shrink-0 transition-transform duration-200 ${aberto ? 'rotate-180' : ''}`} />
            </button>

            {aberto && (
                <div className="absolute z-[1200] mt-1.5 left-0 min-w-full w-max max-w-[320px] bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-xl overflow-hidden origin-top animate-[dropdown_120ms_ease-out]">
                    <style>{`@keyframes dropdown{from{opacity:0;transform:translateY(-4px) scale(.98)}to{opacity:1;transform:none}}`}</style>

                    <div className="relative border-b border-gray-100 dark:border-gray-700">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            ref={inputRef}
                            value={busca}
                            onChange={(e) => { setBusca(e.target.value); setDestaque(0); }}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            className="w-full pl-8 pr-3 py-2 text-xs bg-transparent text-gray-800 dark:text-gray-100 focus:outline-none"
                        />
                    </div>

                    <div ref={listRef} className="max-h-60 overflow-y-auto py-1">
                        {filtradas.length === 0 ? (
                            <p className="px-3 py-4 text-xs text-gray-400 text-center">Nada encontrado</p>
                        ) : filtradas.map((o, i) => {
                            const ativo = o.value === value;
                            return (
                                <button
                                    key={o.value || '__all__'}
                                    data-i={i}
                                    onMouseEnter={() => setDestaque(i)}
                                    onClick={() => escolher(o.value)}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors
                                        ${i === destaque ? 'bg-primary/10' : ''}
                                        ${ativo ? 'font-bold text-primary' : 'text-gray-700 dark:text-gray-200'}`}
                                >
                                    <span className="truncate flex-1">{o.label}</span>
                                    {o.count != null && (
                                        <span className="text-[10px] text-gray-400 shrink-0">{o.count.toLocaleString('pt-BR')}</span>
                                    )}
                                    {ativo && <Check size={12} className="text-primary shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
